/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import Employees from './Employees';
import Login from './Login';
import Settings from './Settings';
import { getOrCreateCompany } from './company';
import { supabase } from './supabase';
import EmployeeView from './EmployeeView';
import Requests from './Requests';
import Dashboard from './Dashboard';
import Messages from './Messages';
import OnboardingWizard from './OnboardingWizard';
import Operations from './Operations';
import Inventory from './Inventory';
import Menu from './Menu';
import Home from './Home';
import { NAV_EVENT, OPEN_INVOICE_REVIEW_EVENT, openInvoiceReview } from './lib/navigate';
import { ToastQueue, TOAST_LIVE_DEFAULT_MS, TOAST_LIVE_PACK_MS } from './components/systemKit';
import InvoiceReviewModal from './components/InvoiceReviewModal';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import Paywall from './components/Paywall';
import TrialBanner from './components/TrialBanner';
import WelcomeModal from './components/WelcomeModal';
import BillingSuccess from './components/BillingSuccess';
import BillingCanceled from './components/BillingCanceled';
import { useBillingState } from './lib/billing';

const DEV_EMAIL = 'austinmr88@gmail.com';

// Sidebar nav structure. Five groups, every sub-item rendered as a direct
// link (always-visible). See DESIGN_SYSTEM.md §Three-group nav.
//
// Top-level pages: home, dashboard, requests, messages, operations, inventory,
// menu, employees, settings. Operations / Inventory / Menu each host multiple
// sub-tabs; the sidebar pre-selects the right tab when you click into one.
const NAV_GROUPS = [
  {
    key: 'scheduling',
    label: 'Scheduling',
    items: [
      { page: 'dashboard', label: 'Schedule' },
      { page: 'requests',  label: 'Requests',  badgeKey: 'pendingRequestCount' },
      { page: 'messages',  label: 'Messages',  badgeKey: 'unreadMessageCount' },
    ],
  },
  {
    key: 'operations',
    label: 'Operations',
    items: [
      { page: 'operations', tab: 'overview', label: 'Overview' },
      { page: 'operations', tab: 'sales',    label: 'Sales' },
      { page: 'operations', tab: 'food',     label: 'Food Costs' },
      { page: 'operations', tab: 'labor',    label: 'Labor Costs' },
    ],
  },
  {
    key: 'inventory',
    label: 'Inventory',
    items: [
      { page: 'inventory', tab: 'dashboard',    label: 'Dashboard' },
      { page: 'inventory', tab: 'pricetracker', label: 'Price Tracker' },
      { page: 'inventory', tab: 'trends',       label: 'Inventory Trends' },
      { page: 'inventory', tab: 'invoices',     label: 'Invoices', badgeKey: 'pendingInvoicesCount' },
    ],
  },
  {
    key: 'menu',
    label: 'Menu',
    items: [
      { page: 'menu', tab: 'profitability', label: 'Menu' },
      { page: 'menu', tab: 'health',        label: 'Recipe Health' },
      { page: 'menu', tab: 'coach',         label: 'Menu Coach' },
    ],
  },
  {
    key: 'manage',
    label: 'Manage',
    items: [
      { page: 'employees', label: 'Employees' },
      { page: 'settings',  label: 'Settings' },
    ],
  },
];

// findGroupForPage — returns the NAV_GROUPS key whose items include `page`.
// Used by the smart-default initializer (expand only the group containing
// the active page on first load) and by the auto-expand-on-navigate logic
// in the app:navigate listener.
function findGroupForPage(page) {
  for (const g of NAV_GROUPS) {
    if (g.items.some(item => item.page === page)) return g.key;
  }
  return null;
}

function App() {
  const [currentPage, setCurrentPage] = useState('home');
  // Sub-tab state for each multi-surface top-level page. Pages outside the
  // hub can deep-link via the app:navigate CustomEvent.
  const [operationsTab, setOperationsTab] = useState('overview');
  const [inventoryTab, setInventoryTab] = useState('dashboard');
  const [menuTab, setMenuTab] = useState('profitability');
  // One-shot navigation payload — Invoices reads `period` / `anchor` here
  // when the operator clicks "Open invoices →" from the Inventory Trends
  // compact strip. Consumed by a useEffect on the receiving page, then
  // cleared via setNavConfig(null) so re-navigating doesn't re-trigger.
  const [navConfig, setNavConfig] = useState(null);
  // Sidebar group expansion. Lazy initializer applies the smart default:
  // expand only the group containing the initial page. No persistence —
  // resets every full reload. See DESIGN_SYSTEM.md §Sidebar collapsible groups.
  const [expandedGroups, setExpandedGroups] = useState(() => {
    const initial = findGroupForPage('home'); // currentPage initial value
    return new Set(initial ? [initial] : []);
  });
  const expandGroupForPage = (page) => {
    const groupKey = findGroupForPage(page);
    if (!groupKey) return;
    setExpandedGroups(prev => prev.has(groupKey) ? prev : new Set([...prev, groupKey]));
  };
  const toggleGroup = (groupKey) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupKey)) next.delete(groupKey); else next.add(groupKey);
      return next;
    });
  };
  const [authUser, setAuthUser] = useState(null);
  const [loggedIn, setLoggedIn] = useState(false);
  // Signed-out auth flow view. 'login' is the default. 'forgot-password' is
  // reached via the link on Login (state-only, no URL change). 'reset-password'
  // is the email-link landing target — initialized lazily from the
  // ?view=reset-password URL param so a fresh tab landing on the email link
  // renders the password-update form BEFORE the auth gate (the operator's
  // signed-out; the recovery token is in the URL fragment, which the Supabase
  // JS client picks up automatically). See src/components/ResetPassword.js
  // for the token-handling specifics.
  // ?view= URL params land on:
  //   reset-password   → signed-out password reset (Supabase email link)
  //   billing-success  → post-Stripe-Checkout success view (polls webhook)
  //   billing-canceled → post-Stripe-Checkout cancel view
  // Billing views require an authenticated session (Stripe redirected
  // back into the app after auth was already established). If we're
  // signed out when we land there, the auth gate prompts for login first
  // and the URL param is preserved through the round-trip.
  const [authView, setAuthView] = useState(() => {
    if (typeof window === 'undefined') return 'login';
    try {
      const params = new URLSearchParams(window.location.search);
      const v = params.get('view');
      if (v === 'reset-password') return 'reset-password';
      if (v === 'billing-success') return 'billing-success';
      if (v === 'billing-canceled') return 'billing-canceled';
    } catch (_) { /* malformed URL — fall through to login */ }
    return 'login';
  });
  const [userRole, setUserRole] = useState('manager');
  const [companyId, setCompanyId] = useState(null);
  const [viewMode, setViewMode] = useState('manager');
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('posted-dark') === 'true');
  const [userEmail, setUserEmail] = useState('');
  const [devEmployeeView, setDevEmployeeView] = useState(false);
  const [devPreviewId, setDevPreviewId] = useState(null);
  const [devRoster, setDevRoster] = useState([]);
  const [devSearch, setDevSearch] = useState('');
  const [devRoleFilter, setDevRoleFilter] = useState('All');
  const [devRoles, setDevRoles] = useState([]);
  const [needsOnboarding, setNeedsOnboarding] = useState(null);
  const [pendingRequestCount, setPendingRequestCount] = useState(0);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [pendingInvoicesCount, setPendingInvoicesCount] = useState(0);
  // Welcome-modal one-shot. Fetched once when companyId resolves; flipped
  // to true here when the operator clicks Skip (the modal also writes to
  // companies.has_seen_welcome so a refresh doesn't re-show it).
  const [hasSeenWelcome, setHasSeenWelcome] = useState(true); // default true so we never flash the modal pre-fetch
  const [welcomeFetched, setWelcomeFetched] = useState(false);
  // Live toast queue for the invoice-parse notification. Each entry is a
  // RichToast-shaped object plus an `id` (string). `invoiceIds` carries
  // the underlying invoice ids merged into this toast (1+ when multiple
  // invoices parse within the 30s debounce window) so the CTA knows what
  // to open. See systemKit RichToast / ToastQueue.
  const [liveToasts, setLiveToasts] = useState([]);
  // The Invoice Review modal is owned at App root so any surface (toast
  // CTA, Invoices row click) can open it via the OPEN_INVOICE_REVIEW
  // CustomEvent. null when closed.
  const [reviewInvoiceId, setReviewInvoiceId] = useState(null);

  useEffect(() => {
    if (!companyId) return;
    fetchBadgeCounts();
    const interval = setInterval(fetchBadgeCounts, 30000);
    return () => clearInterval(interval);
  }, [companyId]);

  // Top-level cross-component navigation listener. All cross-page links
  // dispatch `app:navigate` with `{ page, tab?, config? }`; this routes the
  // payload to the right state setters. See src/lib/navigate.js.
  //
  // Auto-expand: the destination page's group is added to expandedGroups
  // synchronously alongside the page state update — without it, the
  // sidebar's active sub-item wouldn't render and the active state would
  // appear nowhere.
  useEffect(() => {
    const handler = (e) => {
      const { page, tab, config } = e.detail || {};
      if (page) {
        setCurrentPage(page);
        expandGroupForPage(page);
      }
      if (tab) {
        if (page === 'operations' || (!page && currentPage === 'operations')) setOperationsTab(tab);
        else if (page === 'inventory' || (!page && currentPage === 'inventory')) setInventoryTab(tab);
        else if (page === 'menu' || (!page && currentPage === 'menu')) setMenuTab(tab);
      }
      if (config !== undefined) setNavConfig(config);
    };
    window.addEventListener(NAV_EVENT, handler);
    return () => window.removeEventListener(NAV_EVENT, handler);
  }, [currentPage]);

  async function fetchBadgeCounts() {
    if (!companyId) return;
    const [reqRes, msgRes, swapRes] = await Promise.all([
      supabase.from('requests').select('id').eq('company_id', companyId).eq('status', 'pending'),
      supabase.from('messages').select('id').eq('company_id', companyId).eq('read', false).not('sender_id', 'is', null),
      supabase.from('swap_requests').select('id').eq('company_id', companyId).eq('status', 'pending_manager'),
    ]);
    setPendingRequestCount((reqRes.data?.length || 0) + (swapRes.data?.length || 0));
    setUnreadMessageCount(msgRes.data?.length || 0);
  }

  useEffect(() => {
    if (!companyId) return;
    (async () => {
      const { data: emps } = await supabase
        .from('employees')
        .select('id')
        .eq('company_id', companyId)
        .limit(1);
      const { data: staffing } = await supabase
        .from('staffing_levels')
        .select('id')
        .eq('company_id', companyId)
        .limit(1);
      setNeedsOnboarding((!emps || emps.length === 0) && (!staffing || staffing.length === 0));
    })();
  }, [companyId]);

  // ─── Live invoice parse notification ───────────────────────────────────
  //
  // Subscribes to UPDATEs on `invoices` filtered by company_id. The
  // FoodPriceTracker upload pipeline (see saveInvoiceItems step 6e)
  // stamps parse_summary + parse_summary_at + review_status='pending'
  // as the LAST step of upload, so this push fires once per invoice
  // with a fully-populated summary blob ready to render.
  //
  // ── Replay protection ────────────────────────────────────────────────
  // Realtime can replay UPDATEs (reconnects, browser tab reopen). To
  // avoid re-toasting an invoice the operator already saw:
  //
  //   1. parse_summary_at TTL: ignore pushes older than 5 min. The
  //      operator either saw it live or they didn't — no point bugging
  //      them about a 20-minute-old parse.
  //
  //   2. seenInvoiceIdsRef: session-local Set of invoice ids we've
  //      already toasted. A reconnect that replays the same row hits
  //      the guard and bails.
  //
  // ── Eager-edge merge / 30s consolidation window ──────────────────────
  // Operators sometimes upload 5 invoices at once. Without coalescing,
  // they get 5 stacked toasts in 8 seconds — annoying. But we also can't
  // make the operator wait 30s to see the first toast (that's the bug
  // shipped in 02f6dfc and fixed here).
  //
  // Eager-edge semantics:
  //   1. First push within an idle period emits a toast IMMEDIATELY.
  //   2. The toast acquires a 30s consolidation window. Any pushes
  //      within the window mutate the visible toast in place — its
  //      headline/detail update, the operator never sees a "new" toast.
  //   3. After 30s of quiet, the window closes. The next push starts a
  //      fresh toast with its own window.
  //
  // The toast queue (multi-toast stacking) covers the case where pushes
  // arrive AFTER an earlier toast's window has closed — the new toast
  // stacks underneath rather than overwriting.
  const DEBOUNCE_MS = 30000;
  const PARSE_TTL_MS = 5 * 60 * 1000;
  const seenInvoiceIdsRef = useRef(new Set());
  // Active consolidation window: { rows, currentToastId, windowTimer }.
  // currentToastId === null when no window is active (idle); a push in
  // that state starts a new window.
  const pendingMergeRef = useRef({ rows: [], currentToastId: null, windowTimer: null });

  // Derive moved_count defensively. If the persisted summary somehow
  // omitted moved_count (manual SQL injection during testing, or a
  // bug in computeInvoiceSummary), fall back to summing the
  // constituent class counts. Single source of truth — used by both
  // the toast logic AND the InvoiceBrowser chip.
  function getMovedCount(summary) {
    if (!summary) return 0;
    if (typeof summary.moved_count === 'number') return summary.moved_count;
    return (Number(summary.up) || 0)
      + (Number(summary.down) || 0)
      + (Number(summary.pack_changed) || 0)
      + (Number(summary.new_item) || 0);
  }

  // Build the toast object from the current accumulated rows.
  // Pure — no state side-effects.
  const buildToastFromRows = useCallback((rows, toastId) => {
    const totalMoved = rows.reduce((s, r) => s + getMovedCount(r.parse_summary), 0);
    const hasRedFlag = rows.some(r => r.parse_summary?.has_red_flag);
    const hasPackChange = rows.some(r => (r.parse_summary?.pack_changed || 0) > 0);
    const tone = hasRedFlag ? 'red' : hasPackChange ? 'amber' : (totalMoved > 0 ? 'amber' : 'neutral');
    const dismissMs = hasPackChange ? TOAST_LIVE_PACK_MS : TOAST_LIVE_DEFAULT_MS;

    let headline;
    let detail;
    if (rows.length === 1) {
      const r = rows[0];
      const vendor = (r.vendor_name || 'Invoice').replace(/\n/g, ' / ');
      headline = `${vendor} invoice parsed`;
      const s = r.parse_summary || {};
      const moved = getMovedCount(s);
      const parts = [];
      if (moved > 0) parts.push(`${moved} item${moved === 1 ? '' : 's'} moved`);
      if (s.total_cost_change) {
        const sign = s.total_cost_change > 0 ? '+' : '−';
        parts.push(`${sign}$${Math.abs(s.total_cost_change).toFixed(2)} cost change`);
      }
      if (s.has_red_flag) parts.push('trend alert');
      if (s.pack_change_examples?.length > 0) {
        const ex = s.pack_change_examples[0];
        parts.push(`pack ${ex.from} → ${ex.to}`);
      }
      detail = parts.join(' · ') || 'Tap to review.';
    } else {
      const vendors = [...new Set(rows.map(r => (r.vendor_name || '').replace(/\n/g, ' / ').trim()).filter(Boolean))];
      headline = `${rows.length} invoices parsed`;
      detail = `${vendors.slice(0, 3).join(', ')}${vendors.length > 3 ? ', …' : ''} · ${totalMoved} item${totalMoved === 1 ? '' : 's'} moved`;
    }

    const invoiceIds = rows.map(r => r.id);
    return {
      id: toastId,
      tone,
      eyebrow: rows.length === 1 ? 'Invoice parsed' : 'Invoices parsed',
      headline,
      detail,
      ctaText: rows.length === 1 ? 'Review' : 'Review latest',
      onCta: () => openInvoiceReview(invoiceIds[invoiceIds.length - 1]),
      _dismissMs: dismissMs,
      _invoiceIds: invoiceIds,
    };
  }, []);

  useEffect(() => {
    if (!companyId) return;
    const channel = supabase
      .channel(`invoices-live-parse-${companyId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'invoices',
          filter: `company_id=eq.${companyId}`,
        },
        (payload) => {
          const row = payload.new;
          if (!row || !row.id || !row.parse_summary) return;
          if (row.review_status !== 'pending') return;
          if (seenInvoiceIdsRef.current.has(row.id)) return;

          // TTL guard against backfill replay.
          if (row.parse_summary_at) {
            const ageMs = Date.now() - new Date(row.parse_summary_at).getTime();
            if (ageMs > PARSE_TTL_MS) return;
          }

          // Suppress when the parsed invoice has nothing notable.
          // Operator still sees it in Unreviewed; we just don't toast
          // about a flat-on-flat invoice.
          if (getMovedCount(row.parse_summary) === 0) {
            seenInvoiceIdsRef.current.add(row.id);
            // Still bump the pending count for the sidebar badge.
            setPendingInvoicesCount(c => c + 1);
            return;
          }

          seenInvoiceIdsRef.current.add(row.id);
          pendingMergeRef.current.rows.push(row);
          setPendingInvoicesCount(c => c + 1);

          if (pendingMergeRef.current.currentToastId) {
            // Window is active — mutate the visible toast in place.
            const updatedToast = buildToastFromRows(
              pendingMergeRef.current.rows,
              pendingMergeRef.current.currentToastId
            );
            setLiveToasts(prev => prev.map(t => t.id === updatedToast.id ? updatedToast : t));
          } else {
            // Idle → emit immediately and open a 30s consolidation window.
            const toastId = `live-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
            pendingMergeRef.current.currentToastId = toastId;
            const newToast = buildToastFromRows(pendingMergeRef.current.rows, toastId);
            setLiveToasts(prev => [...prev, newToast]);
            // Auto-dismiss after the toast's own dismissMs.
            setTimeout(() => {
              setLiveToasts(prev => prev.filter(t => t.id !== toastId));
            }, newToast._dismissMs);
            // Close the consolidation window after DEBOUNCE_MS so the
            // next push starts a fresh toast (rather than mutating a
            // toast the operator may have already dismissed).
            pendingMergeRef.current.windowTimer = setTimeout(() => {
              pendingMergeRef.current = { rows: [], currentToastId: null, windowTimer: null };
            }, DEBOUNCE_MS);
          }
        }
      )
      .subscribe();
    return () => {
      if (pendingMergeRef.current.windowTimer) {
        clearTimeout(pendingMergeRef.current.windowTimer);
      }
      pendingMergeRef.current = { rows: [], currentToastId: null, windowTimer: null };
      supabase.removeChannel(channel);
    };
  }, [companyId, buildToastFromRows]);

  // ── Email-inbound passive toast ──────────────────────────────────────
  //
  // Subscribes to inbound_emails and fires a top-right RichToast when a
  // new staged email lands AND the operator isn't currently on the
  // Price Tracker tab (which is where the drain consumer lives — see
  // FoodPriceTracker drain effect). The toast nudges them to open
  // Price Tracker so the queued invoices parse.
  //
  // If the operator IS already on Price Tracker, the drain fires
  // immediately and the existing parse_summary toast handles
  // notification — no need to double-toast.
  //
  // Suppressed for status='received' rows that were already processed
  // (claimed_at set) — those came in via an UPDATE that's just the
  // attachments_json stamp from the webhook's two-step path.
  const inboundSeenRef = useRef(new Set());
  useEffect(() => {
    if (!companyId) return;
    const channel = supabase
      .channel(`inbound-emails-toast-${companyId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'inbound_emails', filter: `company_id=eq.${companyId}` },
        (payload) => {
          const row = payload.new;
          if (!row || row.status !== 'received') return;
          if (inboundSeenRef.current.has(row.id)) return;
          inboundSeenRef.current.add(row.id);

          // If we're already on Price Tracker, the drain handles it —
          // skip the nudge.
          const onPriceTracker = currentPage === 'inventory' && inventoryTab === 'pricetracker';
          if (onPriceTracker) return;

          const toastId = `inbound-${row.id}`;
          const fromShort = String(row.from_address || '').split('@')[0];
          setLiveToasts(prev => [...prev, {
            id: toastId,
            tone: 'amber',
            eyebrow: 'Email invoice received',
            headline: fromShort ? `from ${fromShort}` : 'New email invoice',
            detail: 'Open Price Tracker to parse',
            ctaText: 'Open Price Tracker',
            onCta: () => {
              setCurrentPage('inventory');
              setInventoryTab('pricetracker');
              expandGroupForPage('inventory');
            },
            _dismissMs: TOAST_LIVE_DEFAULT_MS,
          }]);
          setTimeout(() => {
            setLiveToasts(prev => prev.filter(t => t.id !== toastId));
          }, TOAST_LIVE_DEFAULT_MS);
        }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [companyId, currentPage, inventoryTab]);

  // Initial pendingInvoicesCount fetch + periodic refresh. Mirrors the
  // existing fetchBadgeCounts pattern for requests/messages.
  const fetchPendingInvoicesCount = useCallback(async () => {
    if (!companyId) return;
    const { data } = await supabase
      .from('invoices')
      .select('id')
      .eq('company_id', companyId)
      .eq('review_status', 'pending');
    setPendingInvoicesCount(data?.length || 0);
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return;
    fetchPendingInvoicesCount();
    const interval = setInterval(fetchPendingInvoicesCount, 60000);
    return () => clearInterval(interval);
  }, [companyId, fetchPendingInvoicesCount]);

  // Listen for OPEN_INVOICE_REVIEW events from anywhere in the tree.
  useEffect(() => {
    function handler(e) {
      const id = e.detail?.invoiceId;
      if (id) setReviewInvoiceId(id);
    }
    window.addEventListener(OPEN_INVOICE_REVIEW_EVENT, handler);
    return () => window.removeEventListener(OPEN_INVOICE_REVIEW_EVENT, handler);
  }, []);

  // When the modal closes, refresh the pending count — Acknowledge /
  // Dismiss inside the modal both move the invoice out of 'pending'.
  const handleCloseReviewModal = useCallback(() => {
    setReviewInvoiceId(null);
    fetchPendingInvoicesCount();
  }, [fetchPendingInvoicesCount]);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode);
    localStorage.setItem('posted-dark', darkMode);
  }, [darkMode]);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      if (data?.user?.email) setUserEmail(data.user.email);
      if (data?.user) setAuthUser(data.user);
    });
  }, [loggedIn]);

  useEffect(() => {
    if (!devEmployeeView || !companyId) return;
    supabase.from('employees').select('*').eq('company_id', companyId).then(({ data }) => {
      if (data) {
        setDevRoster(data);
        const roles = [...new Set(data.flatMap(e => (typeof e.role === 'string' ? e.role.split(',').map(r => r.trim()) : (e.role || []))))].sort();
        setDevRoles(roles);
        if (!devPreviewId && data.length > 0) setDevPreviewId(data[0].id);
      }
    });
  }, [devEmployeeView, companyId, devPreviewId]);

  const isDev = userEmail === DEV_EMAIL;

  // ── Billing state hook ──────────────────────────────────────────────
  // Reads v_company_billing_state for the operator's company; refetches
  // on focus + every 60s. Drives the paywall gate, trial banner, and
  // billing-settings tab — the single source of truth for "does this
  // company currently have access". Fetched as soon as companyId is
  // known; before that, billing.state is null and gates fall through.
  const { state: billing, refetch: refetchBilling } = useBillingState(companyId);

  // ── Welcome-modal one-shot fetch ────────────────────────────────────
  // Pull companies.has_seen_welcome once. The modal mounts only when
  // false AND the company has no active/trialing subscription yet.
  useEffect(() => {
    if (!companyId || welcomeFetched) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('companies')
        .select('has_seen_welcome')
        .eq('id', companyId)
        .maybeSingle();
      if (cancelled) return;
      setHasSeenWelcome(data ? !!data.has_seen_welcome : true);
      setWelcomeFetched(true);
    })();
    return () => { cancelled = true; };
  }, [companyId, welcomeFetched]);

  // Helper: clear the ?view= URL param after consuming a billing-success
  // / billing-canceled redirect. Keeps the path clean and prevents
  // re-entering the view on a back-button restore.
  const clearAuthViewParam = useCallback(() => {
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('view');
      url.searchParams.delete('session_id');
      window.history.replaceState({}, document.title, url.pathname + (url.search || ''));
    } catch (_) { /* ignore */ }
  }, []);

  // Signed-out auth views — handled BEFORE the loggedIn gate so the
  // password-reset link target works without an active session.
  // ?view=reset-password lands here from the Supabase email; once the
  // token is consumed (or the operator clicks "Back to sign in") we
  // flip authView back to 'login'.
  if (!loggedIn && authView === 'reset-password') {
    return (
      <ResetPassword
        onDone={() => {
          // Wipe the URL param so a refresh doesn't re-enter reset mode.
          try {
            const url = new URL(window.location.href);
            url.searchParams.delete('view');
            window.history.replaceState({}, document.title, url.pathname + (url.search || ''));
          } catch (_) { /* ignore */ }
          setAuthView('login');
        }}
        onRequestNewLink={() => {
          try {
            const url = new URL(window.location.href);
            url.searchParams.delete('view');
            window.history.replaceState({}, document.title, url.pathname + (url.search || ''));
          } catch (_) { /* ignore */ }
          setAuthView('forgot-password');
        }}
      />
    );
  }

  if (!loggedIn && authView === 'forgot-password') {
    return <ForgotPassword onBackToLogin={() => setAuthView('login')} />;
  }

  if (!loggedIn) return <Login
    onForgotPassword={() => setAuthView('forgot-password')}
    onLogin={async (role, user) => {
      setLoggedIn(true);
      setUserRole(role);
      if (user?.email) setUserEmail(user.email);
      if (role === 'manager' && user) {
        try {
          const company = await getOrCreateCompany(user);
          console.log('company returned:', company);
          if (company) setCompanyId(company.id);
        } catch(e) {
          console.log('company error:', e);
        }
      }
    }} />;

  // ── Stripe-Checkout return-trip views ───────────────────────────────
  // These render BEFORE the paywall gate because the success page is the
  // operator's first signal that their subscription succeeded — paywall
  // would be wrong here even if the webhook hasn't fired yet (the
  // success page polls for it). Both views are reachable by URL param
  // only, so we can drop them as soon as the operator hits Continue.
  if (loggedIn && companyId && authView === 'billing-success') {
    return (
      <BillingSuccess
        companyId={companyId}
        onContinue={() => {
          clearAuthViewParam();
          setAuthView('login'); // 'login' is the post-auth-views default
          refetchBilling();
        }}
      />
    );
  }
  if (loggedIn && companyId && authView === 'billing-canceled') {
    return (
      <BillingCanceled
        onContinue={() => {
          clearAuthViewParam();
          setAuthView('login');
        }}
      />
    );
  }

  if (needsOnboarding === null && companyId) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-gray-400 text-sm">Loading...</div>
    </div>;
  }

  if (needsOnboarding) {
    return <OnboardingWizard
      companyId={companyId}
      onComplete={(targetPage) => {
        setNeedsOnboarding(false);
        setCurrentPage(targetPage || 'dashboard');
      }}
    />;
  }

  // ── Paywall gate ────────────────────────────────────────────────────
  // After onboarding, before the main app shell. Renders when billing
  // state has loaded AND has_access === false (trial expired without
  // payment, subscription canceled past period_ends_at, or all dunning
  // retries exhausted to status='unpaid').
  //
  // Note: companies 4 + 10 (development backfill) have status='active'
  // without a stripe_customer_id, so they pass this gate normally. They
  // hit the dev-mode CTA inside Settings → Billing if they want to start
  // a real subscription.
  if (loggedIn && companyId && billing && billing.has_access === false) {
    return <Paywall billing={billing} />;
  }

  if (devEmployeeView && isDev) {
    const filteredRoster = devRoster.filter(e => {
      if (devSearch) {
        const q = devSearch.toLowerCase();
        if (!e.name.toLowerCase().includes(q)) return false;
      }
      if (devRoleFilter !== 'All') {
        const roles = typeof e.role === 'string' ? e.role.split(',').map(r => r.trim()) : (e.role || []);
        if (!roles.includes(devRoleFilter)) return false;
      }
      return true;
    });
    return (
      <div className="min-h-screen bg-gray-50 flex">
        {/* Roster sidebar */}
        <div className="w-56 bg-white border-r border-gray-200 flex flex-col flex-shrink-0">
          <div className="p-3 border-b border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">Preview as</span>
              <button onClick={() => setDevEmployeeView(false)} className="text-xs text-gray-400 border border-dashed border-gray-300 rounded px-2 py-0.5 hover:bg-gray-50">⚙️ Back</button>
            </div>
            <input type="text" placeholder="Search name..." value={devSearch} onChange={e => setDevSearch(e.target.value)}
              className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:border-blue-400 bg-gray-50 mb-1.5" />
            <select value={devRoleFilter} onChange={e => setDevRoleFilter(e.target.value)}
              className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:border-blue-400 bg-gray-50">
              <option value="All">All roles</option>
              {devRoles.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div className="flex-1 overflow-y-auto">
            {filteredRoster.map(emp => (
              <button key={emp.id} onClick={() => setDevPreviewId(emp.id)}
                className={`w-full text-left px-3 py-2.5 border-b border-gray-100 flex items-center gap-2.5 hover:bg-gray-50 ${devPreviewId === emp.id ? 'bg-blue-50' : ''}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0 ${emp.color}`}>{emp.initials}</div>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">{emp.name}</div>
                  <div className="text-xs text-gray-400 truncate">{typeof emp.role === 'string' ? emp.role : (emp.role || []).join(', ')}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
        {/* Employee view */}
        <div className="flex-1 overflow-hidden">
          {devPreviewId && <EmployeeView key={devPreviewId} onManagerView={() => setDevEmployeeView(false)} previewEmployeeId={devPreviewId} />}
        </div>
      </div>
    );
  }

  if (viewMode === 'employee' || userRole === 'employee') return <EmployeeView onManagerView={() => setViewMode('manager')} />;

  // Welcome-modal trigger: first-login operator who hasn't dismissed yet
  // AND doesn't already have a real Stripe customer (which would mean
  // they already saw the modal pre-checkout). hasSeenWelcome defaults
  // to true so we never flash the modal during the fetch.
  const showWelcomeModal = welcomeFetched
    && !hasSeenWelcome
    && billing
    && !billing.stripe_customer_id;

  return (
    <div className="h-screen bg-gray-100 flex flex-col overflow-hidden">
      {/* Trial / canceled-grace / past-due banner. Renders only when
          relevant; nothing when status='active'. See TrialBanner.js
          for the tone-escalation rules. */}
      <TrialBanner billing={billing} />
      <div className="flex-1 flex overflow-hidden">
      <div className="sidebar-dark w-52 flex flex-col no-print" style={{ boxShadow: '3px 0 12px rgba(0,0,0,0.25)', zIndex: 5 }}>
        <div className="px-4 pt-5 pb-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-indigo-600 flex items-center justify-center text-white text-xs font-bold">P</div>
              <span className="text-base font-bold" style={{ color: '#FFFFFF', letterSpacing: '-0.3px' }}>Posted</span>
            </div>
            <button onClick={() => setDarkMode(d => !d)} className="text-sm w-7 h-7 rounded-lg flex items-center justify-center" style={{ color: '#9CA3AF' }} onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'} title={darkMode ? 'Light mode' : 'Dark mode'}>
              {darkMode ? '☀️' : '🌙'}
            </button>
          </div>
          <div className="text-xs mt-1.5 ml-9" style={{ color: '#9CA3AF' }}>The Rustic Table</div>
        </div>
        <nav className="p-2 flex-1 flex flex-col overflow-y-auto">
          <button onClick={() => setCurrentPage('home')}
            className={`nav-item w-full text-left flex items-center gap-2 mb-0.5 mt-1 ${currentPage === 'home' ? 'nav-item-active' : 'text-gray-500'}`}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><path d="M9 22V12h6v10"/></svg>
            Home
          </button>
          {NAV_GROUPS.map((group, gi) => {
            const isExpanded = expandedGroups.has(group.key);
            // Active state on the group header — defensive: shouldn't happen
            // given auto-expand, but if a group is somehow collapsed while
            // its page is active, we hint at "you're in here" via label color.
            const groupContainsActive = group.items.some(item => {
              const tabState = item.page === 'operations' ? operationsTab
                : item.page === 'inventory' ? inventoryTab
                : item.page === 'menu' ? menuTab
                : null;
              return currentPage === item.page && (item.tab == null || tabState === item.tab);
            });
            // Bubble unread/pending badges up to the group header when collapsed,
            // so collapsing SCHEDULING doesn't hide an inbox.
            const groupBadgeTotal = group.items.reduce((sum, item) => {
              if (item.badgeKey === 'pendingRequestCount') return sum + pendingRequestCount;
              if (item.badgeKey === 'unreadMessageCount') return sum + unreadMessageCount;
              if (item.badgeKey === 'pendingInvoicesCount') return sum + pendingInvoicesCount;
              return sum;
            }, 0);
            return (
              <React.Fragment key={group.key}>
                <div className="my-2 mx-2" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}></div>
                <button
                  onClick={() => toggleGroup(group.key)}
                  aria-expanded={isExpanded}
                  className="stat-label px-3 py-2 flex items-center w-full text-left"
                  style={{
                    cursor: 'pointer',
                    background: 'transparent',
                    border: 'none',
                    color: groupContainsActive && !isExpanded ? 'var(--text-primary)' : undefined,
                    transition: 'background 150ms ease',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <span>{group.label}</span>
                  {!isExpanded && groupBadgeTotal > 0 && (
                    <span
                      style={{
                        marginLeft: 8, background: '#EF4444', color: '#FFF', fontSize: 10, fontWeight: 700,
                        borderRadius: 9999, minWidth: 16, height: 16, display: 'inline-flex',
                        alignItems: 'center', justifyContent: 'center', padding: '0 5px',
                      }}
                    >
                      {groupBadgeTotal}
                    </span>
                  )}
                  <svg
                    width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                    aria-hidden
                    style={{
                      marginLeft: 'auto',
                      transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                      transition: 'transform 200ms ease',
                      opacity: 0.7,
                    }}
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>
                {isExpanded && group.items.map(item => {
                  const tabState = item.page === 'operations' ? operationsTab
                    : item.page === 'inventory' ? inventoryTab
                    : item.page === 'menu' ? menuTab
                    : null;
                  const isActive = currentPage === item.page && (item.tab == null || tabState === item.tab);
                  const badge = item.badgeKey === 'pendingRequestCount' ? pendingRequestCount
                    : item.badgeKey === 'unreadMessageCount' ? unreadMessageCount
                    : item.badgeKey === 'pendingInvoicesCount' ? pendingInvoicesCount
                    : 0;
                  const handleClick = () => {
                    setCurrentPage(item.page);
                    expandGroupForPage(item.page);
                    if (item.tab) {
                      if (item.page === 'operations') setOperationsTab(item.tab);
                      else if (item.page === 'inventory') setInventoryTab(item.tab);
                      else if (item.page === 'menu') setMenuTab(item.tab);
                    }
                    if (item.page === 'requests' || item.page === 'messages') {
                      setTimeout(fetchBadgeCounts, 1000);
                    }
                  };
                  return (
                    <button
                      key={`${item.page}:${item.tab || ''}`}
                      onClick={handleClick}
                      className={`nav-item w-full text-left flex items-center gap-2 mb-0.5 ${isActive ? 'nav-item-active' : 'text-gray-500'}`}
                      style={{ position: 'relative' }}
                    >
                      {item.label}
                      {badge > 0 && (
                        <span style={{ background: '#EF4444', color: '#FFF', fontSize: 10, fontWeight: 700, borderRadius: '50%', minWidth: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px', marginLeft: 'auto' }}>{badge}</span>
                      )}
                    </button>
                  );
                })}
                {gi === NAV_GROUPS.length - 1 && <div className="flex-1" />}
              </React.Fragment>
            );
          })}
          {isDev && (
            <button onClick={() => setDevEmployeeView(true)}
              className="w-full text-left flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs border border-dashed mb-1"
              style={{ borderColor: 'rgba(255,255,255,0.15)', color: '#9CA3AF' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              👁 Preview as Employee
            </button>
          )}
          <div className="mx-2 mb-2" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}></div>
          <button onClick={async () => { await supabase.auth.signOut(); setLoggedIn(false); }}
            className="nav-item w-full text-left flex items-center gap-2"
            style={{ color: '#FCA5A5' }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.1)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            Sign out
          </button>
        </nav>
      </div>

      <div className="flex-1 flex flex-col overflow-hidden">
        {currentPage === 'home' && <Home companyId={companyId} user={authUser} setCurrentPage={setCurrentPage} />}
        {currentPage === 'dashboard' && <Dashboard companyId={companyId} onEmployeeView={() => setViewMode('employee')} onNavigate={(page) => setCurrentPage(page)} />}
        {currentPage === 'employees' && <Employees companyId={companyId} />}
        {currentPage === 'operations' && <Operations companyId={companyId} initialTab={operationsTab} key={`ops-${operationsTab}`} />}
        {currentPage === 'inventory' && <Inventory companyId={companyId} initialTab={inventoryTab} navConfig={navConfig} onConsumeNavConfig={() => setNavConfig(null)} key={`inv-${inventoryTab}`} />}
        {currentPage === 'menu' && <Menu companyId={companyId} initialTab={menuTab} navConfig={navConfig} onConsumeNavConfig={() => setNavConfig(null)} key={`menu-${menuTab}`} />}
        {currentPage === 'requests' && <Requests companyId={companyId} onRequestHandled={fetchBadgeCounts} />}
        {currentPage === 'settings' && <Settings companyId={companyId} />}
        {currentPage === 'messages' && <Messages companyId={companyId} onMessagesRead={fetchBadgeCounts} />}
      </div>
      </div>

      {/* Welcome modal — first-login prompt to start the trial. Only
          renders when companies.has_seen_welcome is false AND there's
          no Stripe customer yet (operator hasn't been to Checkout). */}
      {showWelcomeModal && (
        <WelcomeModal
          companyId={companyId}
          onSkip={() => setHasSeenWelcome(true)}
        />
      )}

      {/* Live invoice-parse toast queue (top-right). One queue, one
          handler — every page sees the same alert stream. See
          DESIGN_SYSTEM.md §Toast positions for the two-position rule. */}
      <ToastQueue
        toasts={liveToasts}
        position="top-right"
        onDismiss={(id) => setLiveToasts(prev => prev.filter(t => t.id !== id))}
      />

      {/* Invoice Review modal — globally mounted so it can be opened from
          a transient toast CTA, the Invoices page, or a future deep link. */}
      {reviewInvoiceId && (
        <InvoiceReviewModal
          invoiceId={reviewInvoiceId}
          onClose={handleCloseReviewModal}
        />
      )}
    </div>
  );
}

export default App;